import React from 'react';
import {
  Box,
  CircularProgress,
  Typography,
  Fade,
  LinearProgress,
} from '@mui/material';
import { useThemeColors } from '../theme/hooks';
import AnimatedLogo from './AnimatedLogo';

const AuthLoadingScreen = ({ 
  type = 'login', // 'login', 'logout', 'loading'
  message = null,
  showProgress = false,
  progress = 0,
  timeout = 3000
}) => {
  console.log('🎨 AuthLoadingScreen: Rendering', { type, message, showProgress, progress });
  
  // Always call hooks at the top level
  const colors = useThemeColors();
  
  // Default messages based on type
  const defaultMessages = {
    login: {
      title: 'Signing you in...',
      subtitle: 'Authenticating your credentials',
      steps: [
        'Validating credentials...',
        'Setting up your session...',
        'Loading your workspace...',
        'Almost ready!'
      ]
    },
    logout: {
      title: 'Signing you out...',
      subtitle: 'Securing your session',
      steps: [
        'Clearing session data...',
        'Securing your information...',
        'Redirecting to login...',
        'Goodbye!'
      ]
    },
    loading: {
      title: 'Loading...',
      subtitle: 'Please wait while we set things up',
      steps: [
        'Initializing application...',
        'Loading your data...',
        'Setting up environment...',
        'Ready!'
      ]
    }
  };

  const config = message ? { title: message, subtitle: '', steps: [] } : defaultMessages[type];
  const [currentStep, setCurrentStep] = React.useState(0);

  // Auto-advance steps
  React.useEffect(() => {
    if (config.steps.length === 0) return;
    
    const interval = setInterval(() => {
      setCurrentStep((prev) => (prev + 1) % config.steps.length);
    }, 800);

    return () => clearInterval(interval);
  }, [config.steps.length]);

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh',
        background: colors.gradient.background,
        position: 'relative',
        overflow: 'hidden',
        animation: 'fadeIn 0.5s ease-in-out',
        '@keyframes fadeIn': {
          '0%': {
            opacity: 0,
            transform: 'translateY(20px)',
          },
          '100%': {
            opacity: 1,
            transform: 'translateY(0)',
          },
        },
      }}
    >
      {/* Background Pattern */}
      <Box
        sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'radial-gradient(circle at 20% 80%, rgba(124, 92, 148, 0.1) 0%, transparent 50%), radial-gradient(circle at 80% 20%, rgba(199, 153, 246, 0.1) 0%, transparent 50%)',
          pointerEvents: 'none',
        }}
      />

      {/* Main Content */}
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          textAlign: 'center',
          position: 'relative',
          zIndex: 1,
          maxWidth: 500,
          width: '100%',
          px: 3,
        }}
      >
        {/* Animated Logo */}
        <Fade in timeout={800}>
          <Box sx={{ mb: 4 }}>
            <AnimatedLogo 
              width={100} 
              height={100} 
              animate={true}
              showStroke={true}
              animationType="combined"
            />
          </Box>
        </Fade>

        {/* Title */}
        <Fade in timeout={1000}>
          <Typography
            variant="h4"
            sx={{
              fontWeight: 700,
              color: colors.text.primary,
              mb: 2,
              letterSpacing: '-0.02em',
              fontSize: { xs: '1.8rem', md: '2.125rem' }
            }}
          >
            {config.title}
          </Typography>
        </Fade>

        {/* Subtitle */}
        {config.subtitle && (
          <Fade in timeout={1200}>
            <Typography
              variant="h6"
              sx={{
                color: colors.text.secondary,
                fontWeight: 500,
                mb: 3,
                lineHeight: 1.6,
                fontSize: { xs: '1rem', md: '1.25rem' }
              }}
            >
              {config.subtitle}
            </Typography>
          </Fade>
        )}

        {/* Progress Bar */}
        {showProgress && (
          <Fade in timeout={1400}>
            <Box sx={{ width: '100%', maxWidth: 300, mb: 3 }}>
              <LinearProgress
                variant="determinate"
                value={progress}
                sx={{
                  height: 6,
                  borderRadius: 3,
                  backgroundColor: 'rgba(255, 255, 255, 0.1)',
                  '& .MuiLinearProgress-bar': {
                    borderRadius: 3,
                    background: colors.gradient.primary,
                  },
                }}
              />
              <Typography
                variant="caption"
                sx={{
                  color: colors.text.secondary,
                  mt: 1,
                  display: 'block',
                  textAlign: 'center',
                }}
              >
                {Math.round(progress)}% Complete
              </Typography>
            </Box>
          </Fade>
        )}

        {/* Loading Spinner */}
        <Fade in timeout={1600}>
          <Box sx={{ position: 'relative', mb: 4 }}>
            <CircularProgress 
              size={60} 
              thickness={3}
              sx={{ 
                color: colors.primary.main,
                '& .MuiCircularProgress-circle': {
                  strokeLinecap: 'round',
                }
              }} 
            />
            
            {/* Inner rotating ring */}
            <CircularProgress
              variant="determinate"
              value={25}
              size={60}
              thickness={1}
              sx={{
                position: 'absolute',
                top: 0,
                left: 0,
                color: colors.primary.light,
                opacity: 0.3,
                transform: 'rotate(90deg)',
                '& .MuiCircularProgress-circle': {
                  strokeLinecap: 'round',
                }
              }}
            />
          </Box>
        </Fade>

        {/* Step Indicator */}
        {config.steps.length > 0 && (
          <Fade in timeout={1800}>
            <Box sx={{ mb: 3, minHeight: '2rem' }}>
              <Typography
                variant="body1"
                sx={{
                  color: colors.text.secondary,
                  fontWeight: 500,
                  fontSize: '1rem',
                  opacity: 0.9,
                  transition: 'opacity 0.3s ease-in-out',
                }}
              >
                {config.steps[currentStep]}
              </Typography>
            </Box>
          </Fade>
        )}

        {/* Progress Dots */}
        <Fade in timeout={2000}>
          <Box sx={{ display: 'flex', gap: 1.5, mb: 4 }}>
            {[0, 1, 2, 3].map((dot) => (
              <Box
                key={dot}
                sx={{
                  width: 10,
                  height: 10,
                  borderRadius: '50%',
                  backgroundColor: colors.primary.main,
                  opacity: 0.3,
                  animation: `pulse 1.5s ease-in-out infinite ${dot * 0.2}s`,
                  '@keyframes pulse': {
                    '0%, 100%': {
                      opacity: 0.3,
                      transform: 'scale(1)',
                    },
                    '50%': {
                      opacity: 1,
                      transform: 'scale(1.3)',
                    },
                  },
                }}
              />
            ))}
          </Box>
        </Fade>

        {/* Animated Progress Bar */}
        <Fade in timeout={2200}>
          <Box sx={{ width: 250, mb: 2 }}>
            <Box
              sx={{
                width: '100%',
                height: 3,
                backgroundColor: 'rgba(255, 255, 255, 0.1)',
                borderRadius: 2,
                overflow: 'hidden',
                position: 'relative',
              }}
            >
              <Box
                sx={{
                  height: '100%',
                  width: '40%',
                  background: colors.gradient.primary,
                  borderRadius: 2,
                  animation: 'progress 2s ease-in-out infinite',
                  '@keyframes progress': {
                    '0%': {
                      width: '0%',
                      opacity: 0.5,
                    },
                    '50%': {
                      width: '70%',
                      opacity: 1,
                    },
                    '100%': {
                      width: '100%',
                      opacity: 0.5,
                    },
                  },
                }}
              />
            </Box>
          </Box>
        </Fade>

        {/* Footer Text */}
        <Fade in timeout={2400}>
          <Typography
            variant="caption"
            sx={{
              color: colors.text.secondary,
              opacity: 0.7,
              fontSize: '0.75rem',
              textAlign: 'center',
              maxWidth: 300,
            }}
          >
            {type === 'login' && 'Setting up your personalized experience...'}
            {type === 'logout' && 'Thank you for using HyreX. Your session is being secured.'}
            {type === 'loading' && 'Preparing everything you need...'}
          </Typography>
        </Fade>
      </Box>

      {/* Floating Elements */}
      <Box
        sx={{
          position: 'absolute',
          top: '20%',
          left: '10%',
          width: 60,
          height: 60,
          borderRadius: '50%',
          background: 'linear-gradient(45deg, rgba(99, 102, 241, 0.1), rgba(139, 92, 246, 0.1))',
          animation: 'float 3s ease-in-out infinite',
          '@keyframes float': {
            '0%, 100%': {
              transform: 'translateY(0px) rotate(0deg)',
            },
            '50%': {
              transform: 'translateY(-20px) rotate(180deg)',
            },
          },
        }}
      />
      
      <Box
        sx={{
          position: 'absolute',
          bottom: '20%',
          right: '15%',
          width: 40,
          height: 40,
          borderRadius: '50%',
          background: 'linear-gradient(45deg, rgba(199, 153, 246, 0.1), rgba(124, 92, 148, 0.1))',
          animation: 'float 4s ease-in-out infinite reverse',
          '@keyframes float': {
            '0%, 100%': {
              transform: 'translateY(0px) rotate(0deg)',
            },
            '50%': {
              transform: 'translateY(-20px) rotate(180deg)',
            },
          },
        }}
      />
    </Box>
  );
};

export default AuthLoadingScreen;